import './header.less'

import {Background, Image, MediaQuery, m} from '@codeurs/front'
import {Field, Form, Formik} from 'formik'
import {
  Component,
  Fragment,
  FunctionComponent,
  useEffect,
  useRef,
  useState
} from 'react'
import {CategoryType, GeneralData, LinkType, PageData} from 'types'
import {t, useLanguage} from 'util/i18n'
import {PageLink, isActiveNav} from 'util/pagelink'

import styled from '@emotion/styled'
import logoImgFR from 'assets/images/climat-logo.svg'
import logoImgNL from 'assets/images/klimaat-logo.svg'
import logo2050 from 'assets/images/logo-2050.png'
import logoBe from 'assets/images/logo-be.png'
import logoCbam from 'assets/images/logo-cbam.svg'
import AnimateHeight from 'react-animate-height'
import {createPortal} from 'react-dom'
import {useLocation, useNavigate} from 'react-router'
import {style} from 'util/style'
import {Theme} from 'views/theme'
import {AutoHide} from '../../util/autohide'
import {isIE} from '../../util/tools'
import {MobileNav} from './mobilenav'
import {SearchField} from './searchfield'

const styles = style('header', [
  'top',
  'nav',
  'logo',
  'main',
  'subnav',
  'searchcontainer'
])

const HeaderRow = ({children}) => <div class={styles('row')}>{children}</div>

const NavItem = styled(PageLink)`
  &:after {
    color: ${props => props.color};
    background-color: ${props => props.color};
  }
`

const SubNavTitleLink = styled('div')`
  &:hover {
    border-color: ${props => props.color};
  }
`
export type LanguageType = 'nl' | 'fr'

const portalContainer = document.createElement('div')
export const HeaderPortal: FunctionComponent = ({children}) =>
  createPortal(children, portalContainer)

const MaybeBackground = ({
  children,
  image,
  overlay = true,
  copyright,
  blue,
  channel
}) => {
  if (image) {
    const src = typeof image === 'string' ? image : image.src ? image.src : null
    const position =
      (typeof image === 'object' && image.focus) || 'center center'
    if (!src && !copyright) return null

    return (
      <div class={styles('hero-bg').mod({overlay, blue})}>
        {src && (
          <Image
            src={src}
            background={true}
            fit="cover"
            position={position}
            upScale={true}
            alt={
              image.alt ||
              image.title?.replace(/^.*[\\/]|(\.[^/.]+$)/g, '') ||
              ''
            }
          />
        )}
        {copyright && (
          <div class={styles('hero-bg-copyright').mod(channel)}>
            {copyright}
          </div>
        )}
        {children}
      </div>
    )
  }
  return <div class={styles('hero-bg')}>{children}</div>
}

export class Header extends Component<{
  data: PageData & {
    featured_blue?: boolean
  }
}> {
  state = {
    openItem: 0,
    openMenu: false,
    openSearch: false,
    openMobileMenu: false
  }
  render() {
    const {header, title, channel, newsletter} = this.props.data
    const hasDropdown =
      channel === 'documents' ||
      channel === 'presentations' ||
      channel === 'media' ||
      channel === 'news'
    const navigate = useNavigate()
    if (!header) return null
    return (
      <Fragment>
        <AutoHide
          blockAutohide={this.state.openMobileMenu}
          class={styles.nav('mobile').is({open: this.state.openMobileMenu})}
        >
          <MediaQuery
            maxWidth={1023}
            view={() => (
              <Fragment>
                <Formik
                  initialValues={{query: ''}}
                  onSubmit={values => {
                    navigate(`${header.search.url}?query=${values.query}`)
                    this.setState({openSearch: !this.state.openSearch})
                  }}
                >
                  <Form
                    className={styles.searchcontainer.is({
                      open: this.state.openSearch
                    })}
                  >
                    <SearchField white={true} mobile={true}>
                      <Field
                        name="query"
                        placeholder={t('library.search') + title.toLowerCase()}
                        aria-label={t('library.search') + title.toLowerCase()}
                      />
                    </SearchField>
                  </Form>
                </Formik>
                <MobileNav
                  {...this.props.data}
                  mobileIsOpen={this.state.openMobileMenu}
                  searchIsOpen={this.state.openSearch}
                  setSearchOpened={setOpen => {
                    this.setState({openSearch: setOpen})
                  }}
                  setMobileNavOpened={setOpen =>
                    this.setState({openMobileMenu: setOpen})
                  }
                  showNewsletter={!!newsletter?.show}
                />
              </Fragment>
            )}
          />
        </AutoHide>
        <header class={styles.mod({hasdropdown: hasDropdown})}>
          <Theme.Container>
            <HeaderTop {...this.props.data} />
            <HeaderMain {...this.props.data} />
          </Theme.Container>
          <div>
            <HeaderNavFromLaptop
              {...this.props.data}
              openItem={this.state.openItem}
              openMenu={this.state.openMenu}
              setMenu={id => this.setMenu(id)}
              showNewsletter={!!newsletter?.show}
            />
          </div>
        </header>
      </Fragment>
    )
  }

  setMenu(id: number) {
    const newState = this.state.openItem === id ? 0 : id
    if (newState === 0) {
      this.setState({openMenu: false})
      setTimeout(() => this.setState({openItem: newState}), 300)
    } else {
      this.setState({openItem: newState})
      setTimeout(() => this.setState({openMenu: true}), 24)
    }
  }
}

export const HeaderLogo = ({logo, alt, title, mod = '', subsite = ''}) => (
  <div class={styles.logo.mod(mod).mod(subsite)}>
    <Image {...logo} alt={alt} title={title} />
  </div>
)

const HeaderTop: FunctionComponent<PageData> = ({translations, subsite}) => {
  const lang = useLanguage()
  const switchLangs = ['nl', 'fr']
  const hosts = {
    nl: window['__HOST_NL__'],
    fr: window['__HOST_FR__'],
    en: window['__HOST_FR__']
  }
  if (
    subsite === '2050' ||
    subsite === 'error2050' ||
    subsite === 'cbam' ||
    subsite === 'errorcbam'
  )
    switchLangs.push('en')
  return (
    <HeaderRow>
      <div class={styles.top}>
        <div class={styles.top('content')}>
          <div class={styles.top('languages')}>
            {switchLangs.map(language => {
              if (language === lang)
                return (
                  <div class={styles.top('languages-item').is({active: true})}>
                    {language}
                  </div>
                )

              let link = ''
              if (subsite === 'cbam' || subsite === 'errorcbam') {
                if (language === 'en') {
                  link = `//${hosts.fr}/cbam-en`
                } else {
                  link = `//${hosts[language]}/cbam`
                }
              } else if (subsite === '2050') {
                link = `//${hosts[language]}/2050-${language}`
              } else {
                if (translations && translations[language]) {
                  link = translations[language].url
                } else {
                  link = `//${hosts[language]}`
                }
              }

              return (
                <PageLink
                  to={link}
                  className={styles.top('languages-item')}
                  target={'_self'}
                >
                  {language}
                </PageLink>
              )
            })}
          </div>
          <div class={styles.top('content-text')}>
            <span>{t('header.top.text')} : </span>
            <a
              class={styles.top('content-link')}
              href={t('header.top.link')}
              target="_blank"
            >
              www.belgium.be
            </a>
          </div>
          <div class={styles.top('logo')}>
            <Image {...logoBe} alt="Logo BE" />
          </div>
        </div>
      </div>
    </HeaderRow>
  )
}

const HeaderMain: FunctionComponent<GeneralData> = ({
  header,
  menu,
  subsite,
  language
}) => {
  const navigate = useNavigate()
  let isSub = false
  let current = {
    link:
      language === 'en'
        ? '//' +
          (window as Window & typeof globalThis & {__HOST_FR__: string})
            .__HOST_FR__
        : menu && menu.home && menu.home.url
          ? menu.home.url
          : null,
    logo: language === 'nl' ? logoImgNL : logoImgFR,
    logoAlt: language === 'nl' ? 'Logo klimaat' : 'Logo climat',
    logoTitle: language === 'nl' ? 'Klimaat.be' : 'Climate.be'
  }

  let other =
    subsite === 'cbam' || subsite === 'errorcbam'
      ? {
          link: menu && menu['cbam']?.url ? menu['cbam'].url : null,
          logo: logoCbam,
          logoAlt: 'Logo CBAM',
          logoTitle: 'CBAM'
        }
      : {
          link:
            menu && menu['2050'] && menu['2050'].url ? menu['2050'].url : null,
          logo: logo2050,
          logoAlt: language === 'nl' ? 'Logo klimaat 2050' : 'Logo climat 2050',
          logoTitle: language === 'nl' ? 'Klimaat 2050' : 'Climat 2050'
        }

  if (
    subsite === '2050' ||
    subsite === 'error2050' ||
    subsite === 'cbam' ||
    subsite === 'errorcbam'
  ) {
    isSub = true
    const switchCurrent = current
    current = other
    other = switchCurrent
  }
  return (
    <HeaderRow>
      <div class={styles.main}>
        <div class={styles.main('home')}>
          <PageLink to={current.link}>
            <HeaderLogo
              logo={current.logo}
              alt={current.logoAlt}
              title={current.logoTitle}
              mod={'main'}
              subsite={subsite}
            />
          </PageLink>
          <Theme.Wysiwyg class={styles.main('home-text')}>
            {header.text}
          </Theme.Wysiwyg>
        </div>
        <div class={styles.main('cta').mod(subsite)}>
          <PageLink to={other.link}>
            <div class={styles.logo}>
              <Image
                {...other.logo}
                alt={other.logoAlt}
                title={other.logoTitle}
              />
            </div>
            {header.link_text && (
              <div class={styles.main('cta-text')}>{header.link_text}</div>
            )}
          </PageLink>
        </div>
        <Formik
          initialValues={{query: ''}}
          onSubmit={values =>
            navigate(`${header.search.url}?query=${values.query}`)
          }
        >
          <Form class={styles.main('search')}>
            <SearchField>
              <Field
                name="query"
                title="Search field"
                placeholder={header.search.placeholder}
              />
            </SearchField>
          </Form>
        </Formik>
      </div>
    </HeaderRow>
  )
}

export const HeaderNav: FunctionComponent<{
  setMenu: (e) => void
  isCategory: (e) => boolean
  getColor: (e) => string
  links: Array<LinkType>
  btnLinks?: Array<LinkType>
  activeItem: number
  sub2050: boolean
  showNewsletter: boolean
}> = ({
  links,
  setMenu,
  isCategory,
  getColor,
  activeItem,
  btnLinks,
  sub2050,
  showNewsletter
}) => {
  const location = useLocation()
  useEffect(() => {
    setMenu(0)
  }, [location.pathname, location.search])
  return (
    <nav class={styles.nav}>
      <Theme.Container>
        <ul class={styles.nav('links').mod({2050: sub2050})}>
          {links &&
            links.map((link, index) => {
              const color = getColor(link)
              if (link.type === 'entry' && isCategory(link.id))
                return (
                  <li class={styles.nav('links-item')}>
                    <HeaderCategory
                      setMenu={setMenu}
                      color={color}
                      activeItem={activeItem}
                      {...link}
                    />
                  </li>
                )
              return (
                <li class={styles.nav('links-item')}>
                  <NavItem
                    theme={undefined}
                    to={link.url}
                    {...styles
                      .nav('link')
                      .mod({ie: isIE()})
                      .is({active: isActiveNav(link.url)})}
                    color={color}
                    onClick={() => setMenu(0)}
                  >
                    {link.description || link.title}
                  </NavItem>
                </li>
              )
            })}
          {btnLinks &&
            btnLinks.map(link => {
              return (
                <li class={styles.nav('links-item')}>
                  <NavItem
                    theme={undefined}
                    to={link.url}
                    {...styles.nav('btn-link')}
                    onClick={() => setMenu(0)}
                  >
                    {link.description || link.title}
                  </NavItem>
                </li>
              )
            })}
          {showNewsletter && (
            <li class={styles.nav('links-item').mod('newsletter')}>
              <NavItem
                theme={undefined}
                to={'#newsletter'}
                {...styles.nav('link').mod('newsletter')}
                onClick={() => setMenu(0)}
                aria-label={t('newsletter.label')}
              >
                {t('newsletter.btn')}
              </NavItem>
            </li>
          )}
        </ul>
      </Theme.Container>
    </nav>
  )
}

const HeaderSubNav: FunctionComponent<
  CategoryType & {
    setMenu: (e) => void
    openMenu: boolean
  }
> = ({setMenu, openMenu, ...item}) => {
  const [prevHeight, setPrevHeight] = useState(0)
  const subnav = useRef<HTMLDivElement>()
  useEffect(() => {
    if (!openMenu) return
    const currentHeight = subnav.current.getBoundingClientRect().height
    if (currentHeight !== prevHeight) setPrevHeight(currentHeight)
  })
  return (
    <Theme.Container>
      <div class={styles.subnav('bg')}>
        <AnimateHeight
          duration={300}
          height={openMenu ? (prevHeight > 0 ? prevHeight : 'auto') : 0}
        >
          <div class={styles.subnav('container')}>
            <div class={styles.subnav.is({open: openMenu})} ref={subnav}>
              {item.children.map(subcat => (
                <ul class={styles.subnav('subcat')}>
                  {subcat.own_page || item.channel === 'library' ? (
                    <li>
                      <PageLink to={subcat.url} onClick={() => setMenu(0)}>
                        {subcat.image && (
                          <Background
                            class={styles.subnav('subcat-image').toString()}
                            img={subcat.image}
                          />
                        )}
                        <SubNavTitleLink
                          theme={undefined}
                          color={item.color}
                          {...styles.subnav('subcat-title')}
                        >
                          <span className={styles.subnav('subcat-title-item')}>
                            {subcat.title}
                          </span>
                        </SubNavTitleLink>
                      </PageLink>
                    </li>
                  ) : (
                    <li>
                      {subcat.image && (
                        <Background
                          class={styles.subnav('subcat-image').toString()}
                          img={subcat.image}
                        />
                      )}
                      <div class={styles.subnav('subcat-title')}>
                        <span className={styles.subnav('subcat-title-item')}>
                          {subcat.title}
                        </span>
                      </div>
                    </li>
                  )}
                  {subcat.children.map(page => (
                    <li>
                      <PageLink
                        to={page.url}
                        onClick={() => setMenu(0)}
                        class={styles.subnav('item')}
                      >
                        <span className={styles.subnav('item-link')}>
                          {page.title}
                        </span>
                      </PageLink>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </AnimateHeight>
      </div>
    </Theme.Container>
  )
}

const HeaderCategory: FunctionComponent<
  {
    setMenu: (e) => void
    color: string
    id: number
    activeItem: number
  } & LinkType
> = ({setMenu, activeItem, ...item}) => {
  return (
    <NavItem
      theme={undefined}
      to={item.url}
      {...styles
        .nav('link')
        .mod({ie: isIE()})
        .is({active: item.id === activeItem})
        .is({active: isActiveNav(item.url)})}
      color={item.color}
      onClick={e => {
        e.preventDefault()
        setMenu(item.id)
      }}
    >
      {item.description || item.title}
    </NavItem>
  )
}

const HeaderNavFromLaptop: React.FC<
  PageData & {
    openItem: number
    openMenu: boolean
    setMenu: (id: number) => void
    featured_blue?: boolean
    showNewsletter: boolean
  }
> = ({
  subsite,
  header,
  categories,
  channel,
  hero,
  openItem,
  openMenu,
  setMenu,
  featured_blue,
  showNewsletter
}) => {
  return (
    <Fragment>
      <HeaderNav
        setMenu={e => setMenu(e)}
        links={header.nav}
        btnLinks={header.btn_nav}
        isCategory={e => isCategoryById(e, categories)}
        getColor={e => getColorByLink(e, categories)}
        activeItem={openItem}
        sub2050={['2050', 'error2050'].indexOf(subsite) >= 0}
        showNewsletter={showNewsletter}
      />

      <MaybeBackground
        image={hero && hero.image}
        overlay={hero && hero.overlay}
        blue={hero && featured_blue}
        copyright={hero && hero.copyright}
        channel={channel}
      >
        <Fragment>
          {openItem > 0 && (
            <HeaderSubNav
              setMenu={e => setMenu(e)}
              openMenu={openMenu}
              {...categories.find(cat => cat.id === openItem)}
            />
          )}
          {hero && hero.image && (
            <div
              class={styles('hero')
                .mod(channel)
                .mod({overlay: hero && hero.overlay})}
              ref={ref => ref && ref.appendChild(portalContainer)}
            ></div>
          )}
        </Fragment>
      </MaybeBackground>
    </Fragment>
  )
}

const getColorByLink = (link: LinkType, categories) => {
  if (link.type === 'entry') {
    for (const category of categories)
      if (category.id === link.id) return category.color
  }
  return '#f57c00'
}
const isCategoryById = (id, categories) => {
  for (const category of categories)
    if (
      category.id === id &&
      ['category', 'library'].indexOf(category.channel) >= 0
    )
      return true
  return false
}
